import React, { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import type { FormikHelpers } from 'formik';
import { Modal } from '../Modal';
import { Button } from '../Button';
import type { ContactFormModalProps, ContactFormValues } from '../../types';
import contactFormBanner from '../../assets/images/partner-form-banner2.jpg';
import { useToast } from '../../context/ToastContext';
import { COUNTRIES, formatCountryDialCode } from '../../data/countries';
import {
  CAUSE_OPTIONS,
  buildContactFormModalPayload,
  contactFormModalInitialValues,
  validateContactFormModal,
} from '../../data/contactForm';
import { submitWebsiteForm } from '../../utils/websiteForm';

export const ContactFormModal: React.FC<ContactFormModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { showToast } = useToast();
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSubmitError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (
    values: ContactFormValues,
    { resetForm }: FormikHelpers<ContactFormValues>
  ) => {
    setSubmitError(null);

    try {
      await submitWebsiteForm(buildContactFormModalPayload(values));

      resetForm();
      onClose();
      showToast('Thank you! Your form has been submitted successfully.');
    } catch {
      setSubmitError(
        'Something went wrong while submitting the form. Please try again.'
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className='max-w-6xl w-full h-auto'
      showCloseButton={true}
    >
      <div className='flex md:h-[720px]'>
        {/* Left Column - Form */}
        <div className='p-4 md:p-6 overflow-y-auto w-full md:w-3/5'>
          <h2 className='text-3xl font-satoshi-bold text-[#006543] mb-6'>
            Let's work together
          </h2>

          <Formik
            initialValues={contactFormModalInitialValues}
            validate={validateContactFormModal}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, errors, touched }) => (
              <Form className='space-y-4'>
                {/* Name Fields */}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <Field
                      type='text'
                      id='firstName'
                      name='firstName'
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006543] focus:border-transparent ${
                        errors.firstName && touched.firstName
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                      placeholder='First Name *'
                    />
                    <ErrorMessage
                      name='firstName'
                      component='div'
                      className='text-red-500 text-sm mt-1'
                    />
                  </div>

                  <div>
                    <Field
                      type='text'
                      id='lastName'
                      name='lastName'
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006543] focus:border-transparent ${
                        errors.lastName && touched.lastName
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                      placeholder='Last Name *'
                    />
                    <ErrorMessage
                      name='lastName'
                      component='div'
                      className='text-red-500 text-sm mt-1'
                    />
                  </div>
                </div>

                {/* Phone Number */}
                <div>
                  <div className='flex gap-2'>
                    <div className='relative w-[42%] min-w-[8.5rem] max-w-[11rem] sm:max-w-[13rem] shrink-0'>
                      <Field
                        as='select'
                        id='phoneIsoCode'
                        name='phoneIsoCode'
                        className='w-full h-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006543] focus:border-transparent appearance-none bg-white pr-8 text-sm'
                        aria-label='Country code'
                      >
                        {COUNTRIES.map(country => (
                          <option key={country.isoCode} value={country.isoCode}>
                            {formatCountryDialCode(country.dialCode)}{' '}
                            {country.name}
                          </option>
                        ))}
                      </Field>
                      <div className='absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none'>
                        <svg
                          className='w-4 h-4 text-gray-400'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                          aria-hidden='true'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M19 9l-7 7-7-7'
                          />
                        </svg>
                      </div>
                    </div>
                    <Field
                      type='tel'
                      id='phoneNumber'
                      name='phoneNumber'
                      className={`flex-1 min-w-0 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006543] focus:border-transparent ${
                        errors.phoneNumber && touched.phoneNumber
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                      placeholder='Phone Number *'
                    />
                  </div>
                  <ErrorMessage
                    name='phoneNumber'
                    component='div'
                    className='text-red-500 text-sm mt-1'
                  />
                </div>

                {/* Email Address */}
                <div>
                  <Field
                    type='email'
                    id='emailAddress'
                    name='emailAddress'
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006543] focus:border-transparent ${
                      errors.emailAddress && touched.emailAddress
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                    placeholder='Email Address *'
                  />
                  <ErrorMessage
                    name='emailAddress'
                    component='div'
                    className='text-red-500 text-sm mt-1'
                  />
                </div>

                {/* Organisation */}
                <div>
                  <Field
                    type='text'
                    id='organisation'
                    name='organisation'
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006543] focus:border-transparent ${
                      errors.organisation && touched.organisation
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                    placeholder='Organisation * :'
                  />
                  <ErrorMessage
                    name='organisation'
                    component='div'
                    className='text-red-500 text-sm mt-1'
                  />
                </div>

                {/* Location */}
                <div>
                  <Field
                    type='text'
                    id='location'
                    name='location'
                    className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006543] focus:border-transparent'
                    placeholder='Location :'
                  />
                </div>

                {/* Cause Interested In */}
                <div className='relative'>
                  <Field
                    as='select'
                    id='causeInterested'
                    name='causeInterested'
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006543] focus:border-transparent appearance-none bg-white pr-10 ${
                      errors.causeInterested && touched.causeInterested
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                  >
                    <option value='' className='text-gray-500'>
                      Cause Interested In *
                    </option>
                    {CAUSE_OPTIONS.map(cause => (
                      <option
                        key={cause}
                        value={cause}
                        className='text-gray-900'
                      >
                        {cause}
                      </option>
                    ))}
                  </Field>
                  {/* Custom dropdown arrow */}
                  <div className='absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none'>
                    <svg
                      className='w-4 h-4 text-gray-400'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M19 9l-7 7-7-7'
                      />
                    </svg>
                  </div>
                  <ErrorMessage
                    name='causeInterested'
                    component='div'
                    className='text-red-500 text-sm mt-1'
                  />
                </div>

                {/* Brief Message */}
                <div>
                  <Field
                    as='textarea'
                    id='briefMessage'
                    name='briefMessage'
                    rows={4}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006543] focus:border-transparent resize-none ${
                      errors.briefMessage && touched.briefMessage
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                    placeholder='Brief Message *'
                  />
                  <ErrorMessage
                    name='briefMessage'
                    component='div'
                    className='text-red-500 text-sm mt-1'
                  />
                </div>

                {submitError && (
                  <p className='text-red-500 text-sm' role='alert'>
                    {submitError}
                  </p>
                )}

                {/* Submit Button */}
                <div className='pt-2'>
                  <Button
                    type='submit'
                    variant='primary'
                    size='sm'
                    disabled={isSubmitting}
                    // className='w-full'
                  >
                    {isSubmitting ? 'SUBMITTING...' : 'SUBMIT'}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>

        {/* Right Column - Banner Image */}
        <div className='relative overflow-hidden w-2/5 hidden md:flex'>
          <img
            src={contactFormBanner}
            alt="Let's work together"
            className='w-full h-full object-cover rounded-r-2xl'
          />
        </div>
      </div>
    </Modal>
  );
};
